/**
 * Asset catalog manifest (docs/ops-protocol.md, "Phase 5 additions").
 *
 * An Xcode asset catalog (`Examples/<App>/Assets.xcassets`) holds the images
 * and colors a SwiftUI app refers to by name (`Image("dough/plain-full")`,
 * `Color("dough/plain-bg")`). The Swift side only ever sends the name; the
 * dev server and the build turn every catalog into one JSON manifest plus the
 * image files (plugins/asset-catalogs.ts), and the page fetches the manifest
 * before loading the app. The layout engine reads intrinsic sizes from it,
 * the renderer resolves `src` and color values.
 *
 * This module is shared by the browser code and the Node plugin, so it must
 * stay free of DOM and Node APIs.
 */

export type AssetImageKind = 'image' | 'symbol';

export interface AssetImage {
  /** Absolute URL path of the image file (`/catalog/<App>/<file>`). */
  src: string;
  /** Intrinsic size in points (pixel size ÷ `scale`). Irrelevant for `symbol`. */
  width: number;
  height: number;
  /** Scale of the file the manifest picked (2 for `@2x`), so pixels = points × scale. */
  scale: number;
  /** `image` for an `.imageset`; `symbol` for an `.symbolset` (an SVG drawn as a template). */
  kind: AssetImageKind;
}

export interface AssetColor {
  /** CSS color for the light appearance (`rgba(r, g, b, a)`). */
  light: string;
  /** CSS color for the dark appearance; absent when the colorset has none. */
  dark?: string;
}

export interface AssetCatalog {
  /** Keyed by catalog name, namespaced with `/` (`dough/plain-full`). */
  images: Record<string, AssetImage>;
  colors: Record<string, AssetColor>;
}

/** The catalog of an app without one (and of the mock app). */
export const EMPTY_CATALOG: Readonly<AssetCatalog> = Object.freeze({ images: {}, colors: {} });

/** URL directory under which manifests and files are served. */
export const CATALOG_DIR = 'catalog';

/** URL path of an app's manifest (`/catalog/<App>.json`). */
export function catalogPath(app: string): string {
  return `/${CATALOG_DIR}/${app}.json`;
}

/**
 * File name an asset is served under: the namespaced name with every `/`
 * replaced by `__`, plus the extension (`dough__plain-full.png`). Flattening
 * keeps every file of a catalog in one directory.
 */
export function assetFileName(name: string, ext: string): string {
  // `/` becomes `__`; spaces and anything else outside [A-Za-z0-9_.-] become
  // `_`, so the served path needs no URL encoding and matches the request
  // pattern the dev middleware accepts.
  return `${name.replace(/\//g, '__').replace(/[^A-Za-z0-9_.-]/g, '_')}.${ext}`;
}

/** URL path of an asset file (`/catalog/<App>/dough__plain-full.png`). */
export function assetFilePath(app: string, name: string, ext: string): string {
  return `/${CATALOG_DIR}/${app}/${assetFileName(name, ext)}`;
}

/**
 * CSS custom property that carries a catalog color: `--sb-asset-color-<slug>`.
 * The slug keeps letters, digits, `-` and `_`; `/` becomes `__` (like file
 * names) and anything else `_`.
 */
export function assetColorVar(name: string): string {
  const slug = name.replace(/\//g, '__').replace(/[^A-Za-z0-9_-]/g, '_');
  return `--sb-asset-color-${slug}`;
}

/** Cheap structural check for a fetched manifest; anything else is treated as empty. */
export function isAssetCatalog(value: unknown): value is AssetCatalog {
  if (typeof value !== 'object' || value === null) return false;
  const { images, colors } = value as { images?: unknown; colors?: unknown };
  return typeof images === 'object' && images !== null && typeof colors === 'object' && colors !== null;
}
